import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin, supabase } from '@/integrations/supabase/client';

interface User {
  id: string;
  email: string;
  name: string;
  country_code: string;
  age_bracket: string;
  age: number;
  gender: string;
  subscription_status: string;
  subscription_plan_id: string;
  subscription_start_date: string;
  subscription_end_date: string;
  trial_end_date: string;
  created_at: string;
  updated_at: string;
  last_login: string;
  email_verified: boolean;
  is_onboarded: boolean;
  signin_method: string;
  providers: string[];
  verification_status: string;
  conversation_count: number;
  last_conversation_date: string | null;
  categories_used: string[];
  subscription_plans?: {
    name: string;
    price_monthly: number;
    price_yearly: number;
  };
  facility_id: string | null;
  facility_role: 'admin' | 'resident' | 'member' | 'teacher' | null;
  facilities?: {
    name: string;
  };
  active_plan?: string;
  plan_expires_at?: string;
  admin_override?: boolean;
  university_access?: boolean;
  prep_foundation_access?: boolean;
  bible_school_access?: boolean;
  elite_academy_access?: boolean;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price_monthly: number;
  price_yearly: number;
}

const getFacilityRoleBadge = (role: string | null) => {
  if (!role) return null;
  const roleColors: Record<string, string> = {
    'admin': 'bg-red-100 text-red-800',
    'teacher': 'bg-indigo-100 text-indigo-800',
    'resident': 'bg-emerald-100 text-emerald-300',
    'member': 'bg-blue-100 text-blue-800'
  };

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${roleColors[role] || 'bg-gray-100 text-gray-800'}`}>
      {role}
    </span>
  );
};

const PLANS = [
  { value: 'free', label: 'Free' },
  { value: 'homeschool_single', label: 'Standard Curriculum' },
  { value: 'edu_elite', label: 'Elite Academy' },
  { value: 'edu_bundle', label: 'Full Bundle' },
  { value: 'university', label: 'University' },
  { value: 'university_bundle', label: 'University + Standard' },
  { value: 'bible_college', label: 'Bible College' },
];

const STATUSES = ['free', 'trial', 'premium', 'cancelled', 'suspended'];
const DEMO_STUDENT_ID = '00000000-0000-4000-a000-000000000a4a';

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState<'all' | 'verified' | 'unverified'>('all');
  const [onboardingFilter, setOnboardingFilter] = useState<'all' | 'onboarded' | 'not_onboarded'>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'conversation_count' | 'last_conversation' | 'name' | 'country_code' | 'age_bracket' | 'email_verified' | 'subscription_status' | 'signin_method' | 'is_onboarded'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [frozenColumns, setFrozenColumns] = useState<Set<string>>(new Set(['user']));
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem('userManagementColumnOrder');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved column order:', e);
      }
    }
    return [
      'checkbox',
      'user',
      'country',
      'joined',
      'verification',
      'subscription',
      'signin',
      'onboarded',
      'conversations',
      'categories',
      'activity',
      'age_bracket',
      'facility',
      'actions'
    ];
  });
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);
  const [editForm, setEditForm] = useState({
    subscription_status: '',
    subscription_plan_id: '',
    subscription_end_date: ''
  });
  const [editOverrideForm, setEditOverrideForm] = useState<{
    expiry: string,
    note: string,
    university_access: boolean,
    prep_foundation_access: boolean,
    bible_school_access: boolean,
    elite_academy_access: boolean
  }>({
    expiry: '',
    note: '',
    university_access: false,
    prep_foundation_access: false,
    bible_school_access: false,
    elite_academy_access: false
  });
  const [isSavingOverride, setIsSavingOverride] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const result = await callAdmin('get-verified-users', {
        data: {
          sort_by: sortBy,
          sort_order: sortOrder,
        },
      })
      if (result.success) {
        setUsers((result.data as User[]) || [])
      } else {
        setError(result.error || 'Failed to fetch users')
      }
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      const result = await callAdmin('get_subscription_plans')
      if (result.success) setSubscriptionPlans(result.data as SubscriptionPlan[])
    } catch (err) {
      console.error('Error fetching subscription plans:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchSubscriptionPlans();
  }, [fetchUsers, fetchSubscriptionPlans]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Synchronize scrollbars for normal mode
  useEffect(() => {
    if (!isFullScreen) {
      const topScrollbar = document.getElementById('top-scrollbar');
      const mainScrollbar = document.getElementById('main-scrollbar');

      if (topScrollbar && mainScrollbar && tableRef.current) {
        // Set the top scrollbar width to match the table width
        const tableWidth = tableRef.current.scrollWidth;
        const topScrollbarContent = topScrollbar.querySelector('div') as HTMLElement;
        if (topScrollbarContent) {
          topScrollbarContent.style.width = `${tableWidth}px`;
        }

        const syncScroll = (source: HTMLElement, target: HTMLElement) => {
          target.scrollLeft = source.scrollLeft;
        };

        const handleTopScroll = () => syncScroll(topScrollbar, mainScrollbar);
        const handleMainScroll = () => syncScroll(mainScrollbar, topScrollbar);

        topScrollbar.addEventListener('scroll', handleTopScroll);
        mainScrollbar.addEventListener('scroll', handleMainScroll);

        return () => {
          topScrollbar.removeEventListener('scroll', handleTopScroll);
          mainScrollbar.removeEventListener('scroll', handleMainScroll);
        };
      }
    }
  }, [users, isFullScreen]); // Re-run when users data changes to update table width

  // Synchronize scrollbars for full-screen mode
  useEffect(() => {
    if (isFullScreen) {
      const topScrollbar = document.getElementById('top-scrollbar-fullscreen');
      const mainScrollbar = document.getElementById('main-scrollbar-fullscreen');

      if (topScrollbar && mainScrollbar && tableRef.current) {
        // Set the top scrollbar width to match the table width
        const tableWidth = tableRef.current.scrollWidth;
        const topScrollbarContent = topScrollbar.querySelector('div') as HTMLElement;
        if (topScrollbarContent) {
          topScrollbarContent.style.width = `${tableWidth}px`;
        }

        const syncScroll = (source: HTMLElement, target: HTMLElement) => {
          target.scrollLeft = source.scrollLeft;
        };

        const handleTopScroll = () => syncScroll(topScrollbar, mainScrollbar);
        const handleMainScroll = () => syncScroll(mainScrollbar, topScrollbar);

        topScrollbar.addEventListener('scroll', handleTopScroll);
        mainScrollbar.addEventListener('scroll', handleMainScroll);

        return () => {
          topScrollbar.removeEventListener('scroll', handleTopScroll);
          mainScrollbar.removeEventListener('scroll', handleMainScroll);
        };
      }
    }
  }, [users, isFullScreen]); // Re-run when users data changes to update table width

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = !statusFilter || user.subscription_status === statusFilter;

    const matchesVerification = verificationFilter === 'all' ||
      (verificationFilter === 'verified' && user.email_verified) ||
      (verificationFilter === 'unverified' && !user.email_verified);

    const matchesOnboarding = onboardingFilter === 'all' ||
      (onboardingFilter === 'onboarded' && user.is_onboarded) ||
      (onboardingFilter === 'not_onboarded' && !user.is_onboarded);

    return matchesSearch && matchesStatus && matchesVerification && matchesOnboarding;
  });

  const getVerificationStatus = (verified: boolean) => {
    return verified ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        ✅ Verified
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        ❌ Unverified
      </span>
    );
  };

  const getSubscriptionStatus = (status: string) => {
    const statusColors = {
      'free': 'bg-gray-100 text-gray-800',
      'premium': 'bg-blue-100 text-blue-800',
      'cancelled': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getOnboardingStatus = (onboarded: boolean) => {
    return onboarded ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        ✅ Onboarded
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        ⏳ Pending
      </span>
    );
  };

  const getSigninMethod = (method: string, providers: string[] = []) => {
    const methodColors = {
      'email': 'bg-blue-100 text-blue-800',
      'google': 'bg-red-100 text-red-800',
      'facebook': 'bg-indigo-100 text-indigo-800'
    };

    const methodIcons = {
      'email': '📧',
      'google': '🔍',
      'facebook': '📘'
    };

    // Show primary method with icon, and additional providers if any
    const primaryMethod = method || (providers.length > 0 ? providers[0] : 'email');
    const hasMultipleProviders = providers.length > 1;

    return (
      <div className="flex flex-col gap-1">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${methodColors[primaryMethod as keyof typeof methodColors] || 'bg-gray-100 text-gray-800'}`}>
          {methodIcons[primaryMethod as keyof typeof methodIcons] || '❓'} {primaryMethod}
        </span>
        {hasMultipleProviders && (
          <span className="text-xs text-gray-500">
            +{providers.length - 1} more
          </span>
        )}
      </div>
    );
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCountryDisplay = (countryCode: string) => {
    const countryMap: { [key: string]: string } = {
      'US': '🇺🇸 United States',
      'GB': '🇬🇧 United Kingdom',
      'CA': '🇨🇦 Canada',
      'AU': '🇦🇺 Australia',
      'DE': '🇩🇪 Germany',
      'FR': '🇫🇷 France',
      'ES': '🇪🇸 Spain',
      'IT': '🇮🇹 Italy',
      'NL': '🇳🇱 Netherlands',
      'SE': '🇸🇪 Sweden',
      'NO': '🇳🇴 Norway',
      'DK': '🇩🇰 Denmark',
      'FI': '🇫🇮 Finland',
      'JP': '🇯🇵 Japan',
      'KR': '🇰🇷 South Korea',
      'CN': '🇨🇳 China',
      'IN': '🇮🇳 India',
      'ID': '🇮🇩 Indonesia',
      'MY': '🇲🇾 Malaysia',
      'BR': '🇧🇷 Brazil',
      'MX': '🇲🇽 Mexico',
      'PH': '🇵🇭 Philippines',
      'SG': '🇸🇬 Singapore',
      'NZ': '🇳🇿 New Zealand',
      'RU': '🇷🇺 Russia',
      'AE': '🇦🇪 United Arab Emirates',
      'ZA': '🇿🇦 South Africa',
      'AR': '🇦🇷 Argentina'
    };

    return countryMap[countryCode] || `🌍 ${countryCode || 'Unknown'}`;
  };

  const getAgeBracketDisplay = (ageBracket: string) => {
    const bracketMap: { [key: string]: string } = {
      'under_30': '👶 Under 30',
      '30_to_49': '👨‍💼 30-49',
      '50_plus': '👴 50+'
    };

    return bracketMap[ageBracket] || '❓ Unknown';
  };

  const handleRowSelect = (userId: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedRows(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === filteredUsers.length) {
      setSelectedRows(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedRows(new Set(filteredUsers.map(u => u.id)));
      setShowBulkActions(true);
    }
  };

  const toggleColumnFreeze = (columnId: string) => {
    const newFrozen = new Set(frozenColumns);
    if (newFrozen.has(columnId)) {
      newFrozen.delete(columnId);
    } else {
      newFrozen.add(columnId);
    }
    setFrozenColumns(newFrozen);
  };

  const handleSort = (column: 'created_at' | 'conversation_count' | 'last_conversation' | 'name' | 'country_code' | 'age_bracket' | 'email_verified' | 'subscription_status' | 'signin_method' | 'is_onboarded') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();

    if (!draggedColumn || draggedColumn === targetColumnId) {
      setDraggedColumn(null);
      return;
    }

    const newColumnOrder = [...columnOrder];
    const draggedIndex = newColumnOrder.indexOf(draggedColumn);
    const targetIndex = newColumnOrder.indexOf(targetColumnId);

    // Remove dragged column and insert at target position
    newColumnOrder.splice(draggedIndex, 1);
    newColumnOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newColumnOrder);
    localStorage.setItem('userManagementColumnOrder', JSON.stringify(newColumnOrder));
    setDraggedColumn(null);
  };

  const getColumnConfig = (columnId: string) => {
    const configs: { [key: string]: { title: string; sortable: boolean; sortKey?: string; frozen: boolean } } = {
      checkbox: { title: '', sortable: false, frozen: false },
      user: { title: 'User', sortable: true, sortKey: 'name', frozen: frozenColumns.has('user') },
      country: { title: 'Country', sortable: true, sortKey: 'country_code', frozen: frozenColumns.has('country') },
      age_bracket: { title: 'Age Bracket', sortable: true, sortKey: 'age_bracket', frozen: frozenColumns.has('age') },
      verification: { title: 'Verification', sortable: true, sortKey: 'email_verified', frozen: frozenColumns.has('verification') },
      subscription: { title: 'Subscription', sortable: true, sortKey: 'subscription_status', frozen: frozenColumns.has('subscription') },
      signin: { title: 'Sign-in Method', sortable: true, sortKey: 'signin_method', frozen: frozenColumns.has('signin') },
      onboarded: { title: 'Onboarded', sortable: true, sortKey: 'is_onboarded', frozen: frozenColumns.has('onboarded') },
      conversations: { title: 'Conversations', sortable: true, sortKey: 'conversation_count', frozen: frozenColumns.has('conversations') },
      categories: { title: 'Categories Used', sortable: false, frozen: false },
      activity: { title: 'Last Activity', sortable: true, sortKey: 'last_conversation', frozen: frozenColumns.has('activity') },
      joined: { title: 'Joined', sortable: true, sortKey: 'created_at', frozen: frozenColumns.has('joined') },
      facility: { title: 'Facility', sortable: true, sortKey: 'facility_id', frozen: frozenColumns.has('facility') },
      actions: { title: 'Actions', sortable: false, frozen: false }
    };
    return configs[columnId] || { title: columnId, sortable: false, frozen: false };
  };

  const handleBulkCopy = () => {
    const selectedUsers = filteredUsers.filter(user => selectedRows.has(user.id));
    const csvData = selectedUsers.map(user => ({
      'Name': user.name || 'No name',
      'Email': user.email,
      'Country': user.country_code || 'Unknown',
      'Age Bracket': user.age_bracket || 'Unknown',
      'Gender': user.gender || 'Unspecified',
      'Verification': user.email_verified ? 'Verified' : 'Unverified',
      'Subscription': user.subscription_status || 'free',
      'Sign-in Method': user.signin_method || 'email',
      'Onboarded': user.is_onboarded ? 'Yes' : 'No',
      'Conversations': user.conversation_count || 0,
      'Categories Used': user.categories_used?.join(', ') || 'None',
      'Last Activity': user.verification_status === 'Waiting for verification' ? 'Waiting for verification' : user.last_conversation_date ? formatDate(user.last_conversation_date) : 'Never',
      'Joined': formatDate(user.created_at),
      'Facility': user.facilities?.name || 'Personal',
      'Facility Role': user.facility_role || 'Personal Account'
    }));

    // Convert to CSV
    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
    ].join('\n');

    // Copy to clipboard
    navigator.clipboard.writeText(csvContent).then(() => {
      alert(`Copied ${selectedUsers.length} users to clipboard!`);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = csvContent;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(`Copied ${selectedUsers.length} users to clipboard!`);
    });
  };

  const handleBulkExport = () => {
    const selectedUsers = filteredUsers.filter(user => selectedRows.has(user.id));
    const csvData = selectedUsers.map(user => ({
      'Name': user.name || 'No name',
      'Email': user.email,
      'Country': user.country_code || 'Unknown',
      'Age Bracket': user.age_bracket || 'Unknown',
      'Gender': user.gender || 'Unspecified',
      'Verification': user.email_verified ? 'Verified' : 'Unverified',
      'Subscription': user.subscription_status || 'free',
      'Sign-in Method': user.signin_method || 'email',
      'Onboarded': user.is_onboarded ? 'Yes' : 'No',
      'Conversations': user.conversation_count || 0,
      'Categories Used': user.categories_used?.join(', ') || 'None',
      'Last Activity': user.verification_status === 'Waiting for verification' ? 'Waiting for verification' : user.last_conversation_date ? formatDate(user.last_conversation_date) : 'Never',
      'Joined': formatDate(user.created_at)
    }));

    // Convert to CSV
    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };


  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      subscription_status: user.subscription_status,
      subscription_plan_id: user.subscription_plan_id || '',
      subscription_end_date: user.subscription_end_date ? user.subscription_end_date.split('T')[0] : ''
    });
    setEditOverrideForm({
      expiry: user.plan_expires_at ? new Date(user.plan_expires_at).toISOString().split('T')[0] : '',
      note: '',
      university_access: !!user.university_access,
      prep_foundation_access: !!user.prep_foundation_access,
      bible_school_access: !!user.bible_school_access,
      elite_academy_access: !!user.elite_academy_access,
    });
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) {
      return
    }

    try {
      const response = await fetch('https://lpwjdezzaylhiuyyugqt.supabase.co/functions/v1/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          action: 'delete_user',
          userId: userId
        })
      })

      const result = await response.json()

      if (result.success) {
        alert('User deleted successfully')
        fetchUsers() // Refresh the user list
      } else {
        alert(`Error deleting user: ${result.error}`)
      }
    } catch (error) {
      console.error('Error deleting user:', error)
      alert('Failed to delete user. Please try again.')
    }
  }

  const handleVerifyUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Are you sure you want to manually verify user ${userEmail}?`)) {
      return
    }

    try {
      const response = await fetch('https://lpwjdezzaylhiuyyugqt.supabase.co/functions/v1/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          action: 'verify_user_manually',
          userId: userId,
          email: userEmail
        })
      })

      const result = await response.json()

      if (result.success) {
        alert('User verified successfully')
        fetchUsers() // Refresh the user list
      } else {
        alert(`Error verifying user: ${result.error}`)
      }
    } catch (error) {
      console.error('Error verifying user:', error)
      alert('Failed to verify user. Please try again.')
    }
  }

  const saveSubscriptionOverride = async () => {
    if (!selectedUser) return;
    setIsSavingOverride(true);
    try {
      // Auto-derive status: any access grant = premium, otherwise free
      const anyGrant = editOverrideForm.university_access ||
        editOverrideForm.prep_foundation_access ||
        editOverrideForm.bible_school_access ||
        editOverrideForm.elite_academy_access;

      const result = await callAdmin('admin_override_subscription', {
        userId: selectedUser.id,
        subscription_status: anyGrant ? 'premium' : 'free',
        plan_expires_at: editOverrideForm.expiry || null,
        university_access: editOverrideForm.university_access,
        prep_foundation_access: editOverrideForm.prep_foundation_access,
        bible_school_access: editOverrideForm.bible_school_access,
        elite_academy_access: editOverrideForm.elite_academy_access,
        note: editOverrideForm.note,
      });

      if (!result || !result.success) {
        throw new Error(result?.error || 'Override failed — check edge function logs');
      }

      setShowEditModal(false);
      fetchUsers();
      alert('Subscription override applied successfully');
    } catch (err: any) {
      console.error('Error saving subscription override:', err);
      alert(`Failed to save: ${err.message || err}`);
    } finally {
      setIsSavingOverride(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const result = await callAdmin('update_user_subscription', {
        userId: selectedUser.id,
        ...editForm
      })

      if (result.success) {
        setShowEditModal(false);
        fetchUsers(); // Refresh the list
      } else {
        setError(result.error || 'Failed to update user');
      }
    } catch (err) {
      setError('Failed to update user');
      console.error('Error updating user:', err);
    }
  };



  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Full-screen mode
  if (isFullScreen) {
    return (
      <div className="fixed inset-0 bg-slate-950 z-50 flex flex-col text-foreground">
        {/* Full-screen Header */}
        <div className="flex-shrink-0 bg-slate-900/90 backdrop-blur-md border-b border-border p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-bold text-foreground">User Management - Full Screen</h2>
              <div className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-2.5 py-1.5 text-sm bg-slate-900 border border-border text-foreground rounded-lg"
                >
                  <option value="">All Statuses</option>
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select
                  value={verificationFilter}
                  onChange={(e) => setVerificationFilter(e.target.value as 'all' | 'verified' | 'unverified')}
                  className="px-2.5 py-1.5 text-sm bg-slate-900 border border-border text-foreground rounded-lg"
                >
                  <option value="all">All Users</option>
                  <option value="verified">Verified</option>
                  <option value="unverified">Unverified</option>
                </select>
              </div>
            </div>
            <button
              onClick={toggleFullScreen}
              className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              Exit Full Screen
            </button>
          </div>
        </div>

        {/* Full-screen Table */}
        <div className="flex-1 flex flex-col min-h-0 bg-slate-950">
          {/* Top Scrollbar */}
          <div
            className="overflow-x-auto w-full custom-scrollbar"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#475569 #0f172a'
            }}
            id="top-scrollbar-fullscreen"
          >
            <div style={{ height: '1px' }}></div>
          </div>

          {/* Main Table Container */}
          <div
            className="flex-1 overflow-auto w-full custom-scrollbar"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#475569 #0f172a'
            }}
            id="main-scrollbar-fullscreen"
          >
            <table ref={tableRef} className="min-w-full divide-y divide-border">
              <thead className="bg-slate-900/90 backdrop-blur-md sticky top-0 z-10 border-b border-border">
                <tr>
                  {columnOrder.map((columnId) => {
                    const config = getColumnConfig(columnId);
                    const isDragging = draggedColumn === columnId;

                    return (
                      <th
                        key={columnId}
                        className={`px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider ${config.sortable ? 'cursor-pointer hover:bg-slate-800/60' : ''
                          } ${config.frozen ? 'bg-sky-950/40' : ''} ${isDragging ? 'opacity-50' : ''
                          }`}
                        draggable={columnId !== 'checkbox' && columnId !== 'actions'}
                        onDragStart={(e) => handleDragStart(e, columnId)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, columnId)}
                        onClick={config.sortable && config.sortKey ? () => handleSort(config.sortKey as 'created_at' | 'conversation_count' | 'last_conversation' | 'name' | 'country_code' | 'age_bracket' | 'email_verified' | 'subscription_status' | 'signin_method' | 'is_onboarded') : undefined}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            {columnId !== 'checkbox' && columnId !== 'actions' && (
                              <span className="text-muted-foreground cursor-move">⋮⋮</span>
                            )}
                            {columnId === 'checkbox' ? (
                              <input
                                type="checkbox"
                                checked={selectedRows.size === filteredUsers.length && filteredUsers.length > 0}
                                onChange={handleSelectAll}
                                className="rounded border-border bg-slate-900"
                              />
                            ) : (
                              <span>{config.title}</span>
                            )}
                          </div>
                          <div className="flex flex-col">
                            {columnId !== 'checkbox' && columnId !== 'actions' && columnId !== 'categories' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const freezeKey = columnId === 'age_bracket' ? 'age' : columnId;
                                  toggleColumnFreeze(freezeKey);
                                }}
                                className="text-xs hover:text-sky-400"
                              >
                                📌
                              </button>
                            )}
                            {config.sortable && config.sortKey && sortBy === config.sortKey && (
                              <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-slate-950/60 divide-y divide-border">
                {filteredUsers.map((user) => (
                  <tr
                    key={`usr-${user.id}`}
                    className={`hover:bg-slate-900/60 cursor-pointer transition-colors ${selectedRows.has(user.id) ? 'bg-sky-950/30' : ''}`}
                    onClick={() => handleRowSelect(user.id)}
                  >
                    {columnOrder.map((columnId) => {
                      return (
                        <td key={columnId} className="px-2 py-1 whitespace-nowrap text-xs">
                          {columnId === 'checkbox' && (
                            <input
                              type="checkbox"
                              checked={selectedRows.has(user.id)}
                              onChange={() => handleRowSelect(user.id)}
                              className="rounded border-gray-300"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}

                          {columnId === 'user' && (
                            <div>
                              <div className="font-medium text-gray-900 truncate max-w-[150px] flex items-center gap-1.5">
                                <span>{user.name || 'No name'}</span>
                                {(user.id === DEMO_STUDENT_ID || user.email?.includes('ava.demo@')) && (
                                  <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200 shrink-0">
                                    Demo Student
                                  </span>
                                )}
                              </div>
                              <div className="text-gray-500 truncate max-w-[150px]">{user.email}</div>
                            </div>
                          )}

                          {columnId === 'country' && (
                            <div className="text-gray-900 truncate max-w-[100px]">
                              {getCountryDisplay(user.country_code)}
                            </div>
                          )}

                          {columnId === 'age_bracket' && (
                            <div className="text-gray-900">
                              {getAgeBracketDisplay(user.age_bracket)}
                            </div>
                          )}

                          {columnId === 'verification' && getVerificationStatus(user.email_verified)}

                          {columnId === 'subscription' && getSubscriptionStatus(user.subscription_status)}

                          {columnId === 'signin' && getSigninMethod(user.signin_method, user.providers)}

                          {columnId === 'onboarded' && getOnboardingStatus(user.is_onboarded)}

                          {columnId === 'conversations' && (
                            <div className="text-gray-900 text-center">
                              {user.conversation_count}
                            </div>
                          )}

                          {columnId === 'categories' && (
                            <div className="flex flex-wrap gap-1">
                              {user.categories_used.length > 0 ? (
                                user.categories_used.slice(0, 1).map((category, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                  >
                                    {category}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-500">None</span>
                              )}
                              {user.categories_used.length > 1 && (
                                <span className="text-xs text-gray-500">+{user.categories_used.length - 1}</span>
                              )}
                            </div>
                          )}

                          {columnId === 'activity' && (
                            <div className="text-gray-500">
                              {user.verification_status === 'Waiting for verification' ? (
                                <span className="text-yellow-600 font-medium">Waiting</span>
                              ) : user.last_conversation_date ? (
                                formatDate(user.last_conversation_date)
                              ) : (
                                'Never'
                              )}
                            </div>
                          )}

                          {columnId === 'facility' && (
                            <div className="text-xs">
                              {user.facilities ? (
                                <div className="flex flex-col">
                                  <span className="font-semibold text-gray-900">{user.facilities.name}</span>
                                  <span className="text-gray-500 capitalize">{user.facility_role}</span>
                                </div>
                              ) : (
                                <span className="text-gray-400">Personal</span>
                              )}
                            </div>
                          )}

                          {columnId === 'joined' && (
                            <div className="text-gray-500">
                              {formatDate(user.created_at)}
                            </div>
                          )}

                          {columnId === 'actions' && (
                            <div className="font-medium flex space-x-1">
                              <button
                                onClick={() => handleEditUser(user)}
                                className="text-blue-600 hover:text-blue-900 px-1 py-0.5 rounded hover:bg-blue-50"
                              >
                                Edit
                              </button>
                              {!user.email_verified && (
                                <button
                                  onClick={() => handleVerifyUser(user.id, user.email)}
                                  className="text-green-600 hover:text-green-900 px-1 py-0.5 rounded hover:bg-green-50"
                                >
                                  Verify
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteUser(user.id, user.email)}
                                className="text-red-600 hover:text-red-900 px-1 py-0.5 rounded hover:bg-red-50"
                              >
                                Delete
                              </button>
                              {user.facility_id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const path = user.facility_role === 'admin' ? '/facility-admin' : '/family-admin';
                                    window.open(`${path}?facility_id=${user.facility_id}`, '_blank');
                                  }}
                                  className="text-blue-600 hover:text-blue-900 px-1 py-0.5 rounded hover:bg-purple-50"
                                >
                                  Inspect Hub
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Normal mode
  return (
    <div className="flex-1 flex flex-col bg-slate-950/40">
      {/* Header Section */}
      <div className="flex-shrink-0 p-4 bg-slate-900/60 border-b border-border">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground mb-1">User Management</h2>
            <p className="text-sm text-muted-foreground">Manage user accounts and subscriptions</p>
          </div>
          <button
            onClick={toggleFullScreen}
            className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 flex items-center space-x-2 transition-colors shadow-md"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            <span>Full Screen</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div className="bg-slate-900/80 p-3 rounded-xl border border-border">
            <div className="text-center">
              <div className="text-xl font-bold text-foreground">{users.length}</div>
              <div className="text-xs text-muted-foreground font-medium">Total Users</div>
            </div>
          </div>
          <div className="bg-slate-900/80 p-3 rounded-xl border border-border">
            <div className="text-center">
              <div className="text-xl font-bold text-emerald-400">
                {users.filter(u => u.email_verified).length}
              </div>
              <div className="text-xs text-muted-foreground font-medium">Verified Users</div>
            </div>
          </div>
          <div className="bg-slate-900/80 p-3 rounded-xl border border-border">
            <div className="text-center">
              <div className="text-xl font-bold text-sky-400">
                {users.filter(u => u.is_onboarded).length}
              </div>
              <div className="text-xs text-muted-foreground font-medium">Onboarded Users</div>
            </div>
          </div>
          <div className="bg-slate-900/80 p-3 rounded-xl border border-border">
            <div className="text-center">
              <div className="text-xl font-bold text-indigo-400">
                {users.filter(u => u.conversation_count > 0).length}
              </div>
              <div className="text-xs text-muted-foreground font-medium">Active Users</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1.5 text-xs bg-slate-900 border border-border text-foreground rounded-lg focus:ring-1 focus:ring-emerald-500"
            >
              <option value="">All Statuses</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={verificationFilter}
              onChange={(e) => setVerificationFilter(e.target.value as 'all' | 'verified' | 'unverified')}
              className="px-2.5 py-1.5 text-xs bg-slate-900 border border-border text-foreground rounded-lg focus:ring-1 focus:ring-emerald-500"
            >
              <option value="all">All Users</option>
              <option value="verified">Verified Only</option>
              <option value="unverified">Unverified Only</option>
            </select>
            <select
              value={onboardingFilter}
              onChange={(e) => setOnboardingFilter(e.target.value as 'all' | 'onboarded' | 'not_onboarded')}
              className="px-2.5 py-1.5 text-xs bg-slate-900 border border-border text-foreground rounded-lg focus:ring-1 focus:ring-emerald-500"
            >
              <option value="all">All Onboarding</option>
              <option value="onboarded">Onboarded</option>
              <option value="not_onboarded">Not Onboarded</option>
            </select>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field as 'created_at' | 'conversation_count' | 'last_conversation');
                setSortOrder(order as 'asc' | 'desc');
              }}
              className="px-2.5 py-1.5 text-xs bg-slate-900 border border-border text-foreground rounded-lg focus:ring-1 focus:ring-emerald-500"
            >
              <option value="created_at-desc">Newest First</option>
              <option value="created_at-asc">Oldest First</option>
              <option value="conversation_count-desc">Most Active</option>
              <option value="conversation_count-asc">Least Active</option>
              <option value="last_conversation-desc">Recent Activity</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {showBulkActions && selectedRows.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedRows.size} user{selectedRows.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => {
                  setSelectedRows(new Set());
                  setShowBulkActions(false);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkCopy}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                📋 Copy to Clipboard
              </button>
              <button
                onClick={handleBulkExport}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                📥 Export CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table - Full Height */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Top Scrollbar */}
        <div
          className="overflow-x-auto w-full custom-scrollbar mb-0"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e0 #f7fafc'
          }}
          id="top-scrollbar"
        >
          <div style={{ height: '1px' }}></div>
        </div>

        {/* Main Table Container */}
        <div
          className="overflow-x-auto w-full custom-scrollbar"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e0 #f7fafc'
          }}
          id="main-scrollbar"
        >
          <table ref={tableRef} className="min-w-full divide-y divide-border">
            <thead className="bg-slate-900/90 backdrop-blur-md">
              <tr>
                {columnOrder.map((columnId) => {
                  const config = getColumnConfig(columnId);
                  const isDragging = draggedColumn === columnId;

                  return (
                    <th
                      key={columnId}
                      className={`px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider ${config.sortable ? 'cursor-pointer hover:bg-slate-800/60' : ''
                        } ${config.frozen ? 'bg-sky-950/40' : ''} ${isDragging ? 'opacity-50' : ''
                        }`}
                      draggable={columnId !== 'checkbox' && columnId !== 'actions'}
                      onDragStart={(e) => handleDragStart(e, columnId)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, columnId)}
                      onClick={config.sortable && config.sortKey ? () => handleSort(config.sortKey as 'created_at' | 'conversation_count' | 'last_conversation' | 'name' | 'country_code' | 'age_bracket' | 'email_verified' | 'subscription_status' | 'signin_method' | 'is_onboarded') : undefined}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          {columnId !== 'checkbox' && columnId !== 'actions' && (
                            <span className="text-muted-foreground cursor-move">⋮⋮</span>
                          )}
                          {columnId === 'checkbox' ? (
                            <input
                              type="checkbox"
                              checked={selectedRows.size === filteredUsers.length && filteredUsers.length > 0}
                              onChange={handleSelectAll}
                              className="rounded border-border bg-slate-900"
                            />
                          ) : (
                            <span>{config.title}</span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          {columnId !== 'checkbox' && columnId !== 'actions' && columnId !== 'categories' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const freezeKey = columnId === 'age_bracket' ? 'age' : columnId;
                                toggleColumnFreeze(freezeKey);
                              }}
                              className="text-xs hover:text-sky-400"
                            >
                              📌
                            </button>
                          )}
                          {config.sortable && config.sortKey && sortBy === config.sortKey && (
                            <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-slate-950/50 divide-y divide-border">
              {filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className={`hover:bg-slate-900/60 cursor-pointer transition-colors ${selectedRows.has(user.id) ? 'bg-sky-950/30' : ''}`}
                  onClick={() => handleRowSelect(user.id)}
                >
                  {columnOrder.map((columnId) => {
                    return (
                      <td key={columnId} className="px-3 py-2 whitespace-nowrap text-sm">
                        {columnId === 'checkbox' && (
                          <input
                            type="checkbox"
                            checked={selectedRows.has(user.id)}
                            onChange={() => handleRowSelect(user.id)}
                            className="rounded border-gray-300"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}

                        {columnId === 'user' && (
                          <div>
                            <div className="text-sm font-medium text-gray-900 truncate max-w-[200px] flex items-center gap-1.5">
                              <span>{user.name || 'No name'}</span>
                              {(user.id === DEMO_STUDENT_ID || user.email?.includes('ava.demo@')) && (
                                <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200 shrink-0">
                                  Demo Student
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 truncate max-w-[200px]">{user.email}</div>
                          </div>
                        )}

                        {columnId === 'facility' && (
                          <div className="flex flex-col gap-1 items-start">
                            <span className="text-xs font-semibold text-gray-900 truncate max-w-[150px]">
                              {user.facilities?.name || 'Personal'}
                            </span>
                            {getFacilityRoleBadge(user.facility_role)}
                          </div>
                        )}

                        {columnId === 'age_bracket' && (
                          <div className="text-xs text-gray-900">
                            {getAgeBracketDisplay(user.age_bracket)}
                          </div>
                        )}

                        {columnId === 'verification' && getVerificationStatus(user.email_verified)}

                        {columnId === 'subscription' && getSubscriptionStatus(user.subscription_status)}

                        {columnId === 'signin' && getSigninMethod(user.signin_method, user.providers)}

                        {columnId === 'onboarded' && getOnboardingStatus(user.is_onboarded)}

                        {columnId === 'conversations' && (
                          <div className="text-xs text-gray-900 text-center">
                            {user.conversation_count}
                          </div>
                        )}

                        {columnId === 'categories' && (
                          <div className="flex flex-wrap gap-1">
                            {user.categories_used.length > 0 ? (
                              user.categories_used.slice(0, 2).map((category, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {category}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-gray-500">None</span>
                            )}
                            {user.categories_used.length > 2 && (
                              <span className="text-xs text-gray-500">+{user.categories_used.length - 2} more</span>
                            )}
                          </div>
                        )}

                        {columnId === 'activity' && (
                          <div className="text-xs text-gray-500">
                            {user.verification_status === 'Waiting for verification' ? (
                              <span className="text-yellow-600 font-medium">Waiting</span>
                            ) : user.last_conversation_date ? (
                              formatDate(user.last_conversation_date)
                            ) : (
                              'Never'
                            )}
                          </div>
                        )}

                        {columnId === 'joined' && (
                          <div className="text-xs text-gray-500">
                            {formatDate(user.created_at)}
                          </div>
                        )}

                        {columnId === 'actions' && (
                          <div className="text-xs font-medium flex items-center gap-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:text-blue-900 bg-blue-50/50 px-2 py-1 rounded-lg transition-colors"
                            >
                              Edit
                            </button>
                            {user.facility_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const path = user.facility_role === 'teacher' || user.facility_role === 'admin' ? '/facility-admin' : '/family-admin';
                                  window.open(`${path}?facility_id=${user.facility_id}`, '_blank');
                                }}
                                className="text-blue-600 hover:text-blue-900 bg-blue-50/50 px-2 py-1 rounded-lg transition-colors whitespace-nowrap"
                              >
                                Inspect Hub
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              className="text-red-600 hover:text-red-900 bg-red-50/50 px-2 py-1 rounded-lg transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-6 border border-border w-full max-w-lg shadow-2xl rounded-2xl bg-card text-foreground">
            <div>
              <h3 className="text-lg font-bold text-foreground mb-4">
                Edit User: {selectedUser.email}
              </h3>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div className="pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Stripe / Billing Record</h4>
                    <span className="text-[10px] text-gray-400">Does not control product access</span>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Billing Status</label>
                        <select
                          value={editForm.subscription_status}
                          onChange={(e) => setEditForm({ ...editForm, subscription_status: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        >
                          <option value="free">Free</option>
                          <option value="trial">Trial</option>
                          <option value="active">Active</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                        <Input
                          type="date"
                          value={editForm.subscription_end_date}
                          onChange={(e) => setEditForm({ ...editForm, subscription_end_date: e.target.value })}
                          className="w-full text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Linked Stripe Plan</label>
                      <select
                        value={editForm.subscription_plan_id}
                        onChange={(e) => setEditForm({ ...editForm, subscription_plan_id: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                      >
                        <option value="">No plan</option>
                        {subscriptionPlans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} - ${plan.price_monthly}/month
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end mt-3">
                    <Button
                      onClick={handleUpdateUser}
                      className="px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 text-xs"
                    >
                      Update Billing Record
                    </Button>
                  </div>
                </div>

                <div className="pt-3 pb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Access Grants</h4>
                    <div className="flex items-center gap-2">
                      {selectedUser.admin_override && (
                        <span className="text-[10px] bg-red-100 text-red-800 px-2 py-0.5 rounded font-bold uppercase tracking-wider">Override Active</span>
                      )}
                    </div>
                  </div>

                  <div className="bg-blue-50 p-3 rounded-md border border-blue-100 mb-3 text-xs text-blue-800">
                    <strong>ℹ️ How this works:</strong> Tick the products this user should have access to. Status becomes <code>premium</code> automatically when any grant is active. Unticking all sets status back to <code>free</code>.
                  </div>

                  <div className="space-y-2 mb-4">
                    {[
                      { key: 'prep_foundation_access' as const, label: 'Standard Curriculum', sub: 'Prep Foundation homeschool content' },
                      { key: 'elite_academy_access' as const, label: 'Elite Academy', sub: 'Elite homeschool courses' },
                      { key: 'university_access' as const, label: 'StillWaters University BSc.', sub: 'Degree courses on university.stillwaters.ai' },
                      { key: 'bible_school_access' as const, label: 'Bible College', sub: 'Biblical studies curriculum' },
                    ].map(({ key, label, sub }) => (
                      <label key={key} className={`flex items-start space-x-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${editOverrideForm[key] ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}>
                        <input
                          type="checkbox"
                          checked={editOverrideForm[key]}
                          onChange={(e) => setEditOverrideForm({ ...editOverrideForm, [key]: e.target.checked })}
                          className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 flex-shrink-0"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-800">{label}</div>
                          <div className="text-xs text-gray-500">{sub}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    <span className="text-[10px] text-gray-400 uppercase font-bold self-center mr-1">Quick:</span>
                    <button type="button"
                      onClick={() => setEditOverrideForm({ ...editOverrideForm, prep_foundation_access: true, elite_academy_access: true })}
                      className="text-[11px] bg-indigo-50 text-indigo-700 px-2 py-1 rounded border border-indigo-100 hover:bg-indigo-100">
                      Std + Elite
                    </button>
                    <button type="button"
                      onClick={() => setEditOverrideForm({ ...editOverrideForm, university_access: true, prep_foundation_access: true })}
                      className="text-[11px] bg-blue-50 text-sky-300 px-2 py-1 rounded border border-blue-100 hover:bg-blue-100">
                      University + Std
                    </button>
                    <button type="button"
                      onClick={() => setEditOverrideForm({ ...editOverrideForm, university_access: true, prep_foundation_access: true, elite_academy_access: true, bible_school_access: true })}
                      className="text-[11px] bg-green-50 text-green-700 px-2 py-1 rounded border border-green-100 hover:bg-green-100">
                      All Access
                    </button>
                    <button type="button"
                      onClick={() => setEditOverrideForm({ ...editOverrideForm, university_access: false, prep_foundation_access: false, elite_academy_access: false, bible_school_access: false })}
                      className="text-[11px] bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200 hover:bg-gray-200">
                      Clear All
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Access Expires</label>
                      <Input
                        type="date"
                        value={editOverrideForm.expiry}
                        onChange={(e) => setEditOverrideForm({ ...editOverrideForm, expiry: e.target.value })}
                        className="w-full text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Note / Reason</label>
                      <Input
                        type="text"
                        placeholder="Why granting?"
                        value={editOverrideForm.note}
                        onChange={(e) => setEditOverrideForm({ ...editOverrideForm, note: e.target.value })}
                        className="w-full text-sm"
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded p-2 mb-3 text-xs text-gray-500">
                    Will set: <strong>subscription_status = {(editOverrideForm.university_access || editOverrideForm.prep_foundation_access || editOverrideForm.elite_academy_access || editOverrideForm.bible_school_access) ? 'premium' : 'free'}</strong> + admin_override = true
                  </div>

                  <Button
                    onClick={saveSubscriptionOverride}
                    disabled={isSavingOverride}
                    className="w-full py-2 bg-slate-800 text-white hover:bg-slate-900 text-sm font-medium rounded-lg"
                  >
                    {isSavingOverride ? 'Saving...' : '✅ Apply Access Grants'}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end mt-4 pt-4 border-t border-border">
                <Button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground border border-border"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Row Details Modal */}
      {selectedRows.size > 0 && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border text-foreground rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">
                Selected Users ({selectedRows.size})
              </h3>
              <button
                onClick={() => setSelectedRows(new Set())}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-6">
                {filteredUsers.filter(user => selectedRows.has(user.id)).map((user) => (
                  <Card key={user.id} className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Basic Info</h4>
                        <div className="space-y-1 text-sm">
                          <div><strong>Name:</strong> {user.name || 'No name'}</div>
                          <div><strong>Email:</strong> {user.email}</div>
                          <div><strong>ID:</strong> {user.id}</div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Location & Demographics</h4>
                        <div className="space-y-1 text-sm">
                          <div><strong>Country:</strong> {getCountryDisplay(user.country_code)}</div>
                          <div><strong>Age Bracket:</strong> {getAgeBracketDisplay(user.age_bracket)}</div>
                          <div><strong>Gender:</strong> {user.gender || 'Unspecified'}</div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Account Status</h4>
                        <div className="space-y-1 text-sm">
                          <div><strong>Verification:</strong> {getVerificationStatus(user.email_verified)}</div>
                          <div><strong>Subscription:</strong> {getSubscriptionStatus(user.subscription_status)}</div>
                          <div><strong>Onboarded:</strong> {getOnboardingStatus(user.is_onboarded)}</div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Sign-in & Activity</h4>
                        <div className="space-y-1 text-sm">
                          <div><strong>Sign-in Method:</strong> {getSigninMethod(user.signin_method, user.providers)}</div>
                          <div><strong>Conversations:</strong> {user.conversation_count}</div>
                          <div><strong>Last Activity:</strong> {user.verification_status === 'Waiting for verification' ? 'Waiting for verification' : user.last_conversation_date ? formatDate(user.last_conversation_date) : 'Never'}</div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Categories Used</h4>
                        <div className="flex flex-wrap gap-1">
                          {user.categories_used.length > 0 ? (
                            user.categories_used.map((category, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {category}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No categories used</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Timestamps</h4>
                        <div className="space-y-1 text-sm">
                          <div><strong>Joined:</strong> {formatDate(user.created_at)}</div>
                          <div><strong>Updated:</strong> {formatDate(user.updated_at)}</div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-200 z-50"
          title="Scroll to top"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};
